import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { type TChatbotNodeData, type TNodeAction } from '../node.type'
import { EVariableType } from 'src/types/variable'
import { MEDIA_NOT_UPLOADED_HANDLE, MEDIA_UPLOADED_HANDLE, START_NODE_ID } from '../chatbot.constants'
import { type TPatientLocale } from 'src/types/locales'
import { AlertIcon } from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'

function getBranchColorClasses(hasAlert: boolean) {
  return hasAlert
    ? 'c-border-pink-600 c-bg-pink-200 c-text-pink-700'
    : 'c-border-gray-400 c-bg-white c-text-gray-800'
}

function hasReviewAction(condition: TNodeAction | undefined): boolean {
  return !!condition?.requestHumanReviewProblem
}

type TChatbotPreviewNodeProps = {
  id: string
  data: TChatbotNodeData & { selectedLanguage: TPatientLocale }
}

export const ChatbotPreviewNode = memo(function ChatbotPreviewNode({
  id,
  data,
}: Readonly<TChatbotPreviewNodeProps>) {
  const { t } = useTranslation()
  const isFirstNode = id === START_NODE_ID

  const nodeTitle = isFirstNode
    ? t('pages.configuration.followups.chatbot.node-editor.start-node-title')
    : data.title || t('pages.configuration.followups.chatbot.node-editor.new-node')

  const message = data.messagesByLanguage[data.selectedLanguage] || ''

  const hiddenHandleStyle = { opacity: 0, width: 1, height: 1 }

  const RANGE_HANDLE_COLORS = ['#16a34a', '#84cc16', '#eab308', '#f97316', '#dc2626', '#991b1b']

  function badgeHandleStyle(color: string) {
    return { width: 10, height: 10, background: 'white', border: `1.5px solid ${color}`, bottom: -5 }
  }
  const hasVariable = !!data.variable
  const rangeCount =
    data.variable?.type === EVariableType.INTEGER_VALUE_RANGE
      ? (data.variable.condition?.ranges?.length ?? 0)
      : 0

  return (
    <div
      className={twMerge(
        'c-min-w-[280px] c-bg-white c-rounded-lg c-shadow-md c-border c-border-grey-500',
        rangeCount > 4 ? 'c-max-w-none' : 'c-max-w-[360px]',
      )}
    >
      {!isFirstNode && (
        <Handle type="target" position={Position.Top} isConnectable={false} style={hiddenHandleStyle} />
      )}

      <h4 className="c-text-sm c-font-bold c-text-center c-py-2">{nodeTitle}</h4>

      {!isFirstNode && (
        <div className="c-p-2 c-border-t c-border-grey-200">
          {message ? (
            <p className="c-p-3 c-mb-1 c-bg-gray-50 c-rounded c-border c-border-gray-700 c-text-xs c-text-gray-700 c-font-bold c-line-clamp-3">
              {message}
            </p>
          ) : (
            <p className="c-p-3 c-mb-1 c-bg-gray-50 c-rounded c-border c-border-gray-200 c-text-xs c-text-gray-400 c-italic">
              {t('pages.configuration.followups.chatbot.preview.no-message')}
            </p>
          )}
        </div>
      )}

      {data.variable?.type === EVariableType.BOOLEAN && (
        <div className="c-flex c-flex-row c-gap-2 c-p-2 c-border-t c-border-gray-200">
          <div
            className={twMerge(
              'c-flex-1 c-border c-rounded c-p-3 c-relative',
              getBranchColorClasses(hasReviewAction(data.variable.condition?.yes)),
            )}
          >
            <div className="c-text-xs c-font-semibold c-text-center c-flex c-items-center c-justify-center c-gap-1">
              {hasReviewAction(data.variable.condition?.yes) && <AlertIcon className="c-h-3 c-w-3" />}
              {t('pages.configuration.followups.chatbot.node-editor.yes')}
            </div>
            <Handle
              type="source"
              position={Position.Bottom}
              id="yes"
              isConnectable={false}
              style={badgeHandleStyle('#16a34a')}
            />
          </div>
          <div
            className={twMerge(
              'c-flex-1 c-border c-rounded c-p-3 c-relative',
              getBranchColorClasses(hasReviewAction(data.variable.condition?.no)),
            )}
          >
            <div className="c-text-xs c-font-semibold c-text-center c-flex c-items-center c-justify-center c-gap-1">
              {hasReviewAction(data.variable.condition?.no) && <AlertIcon className="c-h-3 c-w-3" />}
              {t('pages.configuration.followups.chatbot.node-editor.no')}
            </div>
            <Handle
              type="source"
              position={Position.Bottom}
              id="no"
              isConnectable={false}
              style={badgeHandleStyle('#dc2626')}
            />
          </div>
        </div>
      )}

      {data.variable?.type === EVariableType.INTEGER_VALUE_RANGE && data.variable.condition?.ranges && (
        <div className="c-flex c-gap-2 c-p-2 c-border-t c-border-gray-200">
          {data.variable.condition.ranges.map((range, index) => (
            <div
              key={`${range.lowerBound}-${range.upperBound}`}
              className={twMerge(
                'c-border c-rounded c-px-4 c-pt-3 c-pb-3 c-flex-1 c-relative',
                getBranchColorClasses(hasReviewAction(range.action)),
              )}
            >
              <div className="c-text-xs c-font-semibold c-text-center c-text-nowrap c-flex c-items-center c-justify-center c-gap-1">
                {hasReviewAction(range.action) && <AlertIcon className="c-h-3 c-w-3" />}
                {t('pages.configuration.followups.chatbot.node-editor.range-label', {
                  from: Number.isNaN(range.lowerBound) ? '?' : range.lowerBound,
                  to: Number.isNaN(range.upperBound) ? '?' : range.upperBound,
                })}
              </div>
              <Handle
                type="source"
                position={Position.Bottom}
                id={`range-${index}`}
                isConnectable={false}
                style={badgeHandleStyle(RANGE_HANDLE_COLORS[index % RANGE_HANDLE_COLORS.length] ?? '#6b7280')}
              />
            </div>
          ))}
        </div>
      )}

      {data.variable?.type === EVariableType.MEDIA && (
        <div className="c-flex c-flex-row c-gap-2 c-p-2 c-border-t c-border-gray-200">
          <div
            className={twMerge('c-flex-1 c-border c-rounded c-p-3 c-relative', getBranchColorClasses(false))}
          >
            <div className="c-text-xs c-font-semibold c-text-center">
              {t('pages.configuration.followups.chatbot.node-editor.media-uploaded')}
            </div>
            <Handle
              type="source"
              position={Position.Bottom}
              id={MEDIA_UPLOADED_HANDLE}
              isConnectable={false}
              style={badgeHandleStyle('#6b7280')}
            />
          </div>
          {data.variable.isRequired === false && (
            <div
              className={twMerge(
                'c-flex-1 c-border c-rounded c-p-3 c-relative',
                getBranchColorClasses(false),
              )}
            >
              <div className="c-text-xs c-font-semibold c-text-center">
                {t('pages.configuration.followups.chatbot.node-editor.media-not-uploaded')}
              </div>
              <Handle
                type="source"
                position={Position.Bottom}
                id={MEDIA_NOT_UPLOADED_HANDLE}
                isConnectable={false}
                style={badgeHandleStyle('#6b7280')}
              />
            </div>
          )}
        </div>
      )}

      {!hasVariable && (
        <Handle type="source" position={Position.Bottom} isConnectable={false} style={hiddenHandleStyle} />
      )}
    </div>
  )
})
